<template>
  <div v-if="newUiEnabled" class="auth-modes">
    <div class="auth-modes__grid">
      <div v-for="option in visibleOptions" :key="option.mode" class="auth-mode">
        <button type="button" class="auth-mode__card" @click="$emit('select', option.mode)">
          <UiIconTile :icon="option.icon" size="l" />

          <span class="auth-mode__title">{{ option.title }}</span>

          <span class="auth-mode__description">{{ option.description }}</span>
        </button>

        <button
          type="button"
          class="auth-link auth-link--button auth-mode__more"
          :aria-expanded="expandedMode === option.mode"
          :aria-controls="`auth-mode-info-${option.mode}`"
          @click="toggleInfo(option.mode)"
        >
          <component
            :is="expandedMode === option.mode ? ChevronUp : ChevronDown"
            :size="16"
            aria-hidden="true"
          />
          {{ t("More info") }}
        </button>

        <ul
          v-if="expandedMode === option.mode"
          :id="`auth-mode-info-${option.mode}`"
          class="auth-mode__info"
        >
          <li v-for="info in option.info" :key="info">{{ info }}</li>
        </ul>
      </div>
    </div>
  </div>

  <div v-else class="mode-options-container text-center">
    <p class="text-muted lead mb-4">{{ t("Choose how you want to register") }}</p>

    <div class="row g-4 justify-content-center">
      <!-- Zamawiam dla siebie -->
      <div v-if="modes.includes('institution_worker')" class="col-12 col-sm-6">
        <div class="mode-card-wrapper">
          <button
            type="button"
            class="mode-card w-100 p-4 d-flex flex-column align-items-center justify-content-center"
            @click="$emit('select', 'institution_worker')"
          >
            <div class="mode-icon mb-3">
              <UiIcon fa="fa fa-user" />
            </div>

            <span class="mode-card-title mb-2">{{ t("I order for myself") }}</span>

            <span class="text-muted small mb-0">
              {{ t("Single user account with one calendar") }}
            </span>
          </button>

          <div class="more-info-toggle mt-2">
            <a href="#" class="small" @click.prevent="showWorkerInfo = !showWorkerInfo">
              <UiIcon
                :fa="'me-1 ' + (showWorkerInfo ? 'fa fa-chevron-up' : 'fa fa-chevron-down')"
              />
              {{ t("More info") }}
            </a>
          </div>

          <b-collapse v-model="showWorkerInfo">
            <div class="more-info-content mt-2 p-3 text-start">
              <ul class="mb-0 ps-3">
                <li>{{ t("registration_info.worker.single_account") }}</li>
                <li>{{ t("registration_info.worker.one_calendar") }}</li>
                <li>{{ t("registration_info.worker.ideal_for_employees") }}</li>
              </ul>
            </div>
          </b-collapse>
        </div>
      </div>

      <!-- Zamawiam dla dziecka -->
      <div v-if="modes.includes('parent')" class="col-12 col-sm-6">
        <div class="mode-card-wrapper">
          <button
            type="button"
            class="mode-card w-100 p-4 d-flex flex-column align-items-center justify-content-center"
            @click="$emit('select', 'parent')"
          >
            <div class="mode-icon mb-3">
              <UiIcon fa="fa fa-users" />
            </div>

            <span class="mode-card-title mb-2">{{ t("I order for child") }}</span>

            <span class="text-muted small mb-0">
              {{ t("Parent account with multiple meal receivers") }}
            </span>
          </button>

          <div class="more-info-toggle mt-2">
            <a href="#" class="small" @click.prevent="showParentInfo = !showParentInfo">
              <UiIcon
                :fa="'me-1 ' + (showParentInfo ? 'fa fa-chevron-up' : 'fa fa-chevron-down')"
              />
              {{ t("More info") }}
            </a>
          </div>

          <b-collapse v-model="showParentInfo">
            <div class="more-info-content mt-2 p-3 text-start">
              <ul class="mb-0 ps-3">
                <li>{{ t("registration_info.parent.multiple_receivers") }}</li>
                <li>{{ t("registration_info.parent.separate_calendars") }}</li>
                <li>{{ t("registration_info.parent.ideal_for_parents") }}</li>
              </ul>
            </div>
          </b-collapse>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ChevronDown, ChevronUp, User, Users } from "lucide-vue-next";
import type { RegistrationMode } from "../queries/useRegisterModesQuery";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";

const props = defineProps<{
  modes: RegistrationMode[];
}>();

defineEmits<{
  select: [mode: RegistrationMode];
}>();

const { t } = useI18n();

const showWorkerInfo = ref(false);
const showParentInfo = ref(false);

const newUiEnabled = useNewUiEnabled();

const expandedMode = ref<RegistrationMode | null>(null);

const toggleInfo = (mode: RegistrationMode): void => {
  expandedMode.value = expandedMode.value === mode ? null : mode;
};

const visibleOptions = computed(() =>
  [
    {
      mode: "institution_worker" as RegistrationMode,
      icon: User,
      title: t("I order for myself"),
      description: t("Single user account with one calendar"),
      info: [
        t("registration_info.worker.single_account"),
        t("registration_info.worker.one_calendar"),
        t("registration_info.worker.ideal_for_employees"),
      ],
    },
    {
      mode: "parent" as RegistrationMode,
      icon: Users,
      title: t("I order for child"),
      description: t("Parent account with multiple meal receivers"),
      info: [
        t("registration_info.parent.multiple_receivers"),
        t("registration_info.parent.separate_calendars"),
        t("registration_info.parent.ideal_for_parents"),
      ],
    },
  ].filter((option) => props.modes.includes(option.mode)),
);
</script>

<style scoped lang="scss">
@import "@/assets/scss/_variables.scss";

.mode-card {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.25s ease;
  min-height: 200px;
}

.mode-card:hover {
  border-color: $primary;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

.mode-card:focus {
  outline: none;
  border-color: $primary;
  box-shadow: 0 0 0 3px rgba($primary, 0.25);
}

.mode-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: #373a3e;
  transition: all 0.25s ease;
}

.mode-card:hover .mode-icon {
  transform: scale(1.15);
  color: $primary;
}

.mode-card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #373a3e;
}

.mode-card:hover .mode-card-title {
  color: $primary;
}

.more-info-toggle {
  text-align: center;

  a {
    color: #6c757d;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: $primary;
    }
  }
}

.more-info-content {
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #495057;

  ul {
    list-style-type: disc;

    li {
      margin-bottom: 0.25rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
